import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  getRedirectResult,
  signInWithRedirect,
  signInWithCredential,
  type User,
} from "firebase/auth";
import { Capacitor } from '@capacitor/core';
import { getFirestore } from "firebase/firestore";
import { getMessaging, type Messaging } from "firebase/messaging";
// import { getAnalytics } from "firebase/analytics"; // optional, only if in browser + proper consent

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Initialize Firebase Cloud Messaging
let messaging: Messaging | null = null;
if (typeof window !== 'undefined') {
  try {
    messaging = getMessaging(app);
  } catch (error) {
    console.warn('FCM not supported in this environment:', error);
  }
}

export { messaging };
// export const analytics = getAnalytics(app); // optional

export async function signInWithGoogle(): Promise<User | null> {
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
  
  // Request additional profile fields
  provider.setCustomParameters({
    prompt: 'select_account'
  });

  // Native (Android/iOS via Capacitor): use the Google Auth native plugin
  if (Capacitor.getPlatform() !== 'web') {
    try {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');

      // Use Credential Manager by default for modern sign-in UX.
      const response: any = await FirebaseAuthentication.signInWithGoogle({ useCredentialManager: true } as any);
      const idToken = response?.credential?.idToken || response?.idToken || response?.authentication?.idToken;
      if (!idToken) {
        throw new Error('Google sign-in failed: No ID token returned');
      }

      const cred = GoogleAuthProvider.credential(idToken);
      const userCred = await signInWithCredential(auth, cred);
      return userCred.user;
    } catch (error) {
      console.error('Native Google Sign-In Error:', error);
      throw error as Error;
    }
  }

  // Web: use popup with redirect fallback
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error: unknown) {
    const authError = error as { code?: string };
    if (
      authError.code === 'auth/popup-blocked' ||
      authError.code === 'auth/cancelled-popup-request'
    ) {
      // When pop-up fails, fallback to redirect method
      await signInWithRedirect(auth, provider);
      return null; // Redirect will cause a page reload, so we return null
    }
    // Handle other errors
    console.error('Google Sign-In Error:', error);
    throw error;
  }
}

export async function completeRedirectSignIn(): Promise<User | null> {
  // Only relevant on the web; native sign-in does not use redirects
  if (Capacitor.getPlatform() !== 'web') return null;
  try {
    const result = await getRedirectResult(auth);
    return result?.user ?? null;
  } catch (error) {
    console.error('Google Redirect Sign-In Error:', error);
    throw error as Error;
  }
}

export function onAuthChange(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback)
}

export async function signOut() {
  await firebaseSignOut(auth)
}
